import type { BulkSupportTicketActionProgressNotification } from "@redotech/effect-api-shared/models/team/user-notification";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTeamNotificationStream } from "../stream/team-notification-stream-provider";
import * as styles from "./bulk-support-ticket-action-progress-bar.module.css";

function getActionLabel(
  action: BulkSupportTicketActionProgressNotification["action"],
): string {
  switch (action._tag) {
    case "setSupportTicketStatus":
      switch (action.status) {
        case "CLOSED":
          return "Closing tickets";
        case "OPEN":
          return "Reopening tickets";
        case "ARCHIVED":
          return "Archiving tickets";
        case "IN_PROGRESS":
          return "Marking tickets in progress";
        default:
          return "Updating tickets";
      }
    case "setReadStatus":
      return action.status === "read"
        ? "Marking tickets read"
        : "Marking tickets unread";
    case "setAssignee":
      return "Assigning tickets";
    case "addTags":
      return "Adding tags to tickets";
    case "removeTags":
      return "Removing tags from tickets";
  }
}

export const BulkSupportTicketActionProgressBar = memo(
  function BulkSupportTicketActionProgressBar() {
    const [eventsByWorkflowId, setEventsByWorkflowId] = useState<
      Record<string, BulkSupportTicketActionProgressNotification>
    >({});
    const completionTimeouts = useRef<
      Map<string, ReturnType<typeof setTimeout>>
    >(new Map());

    useEffect(() => {
      const timeouts = completionTimeouts.current;
      return () => {
        for (const timeout of timeouts.values()) {
          clearTimeout(timeout);
        }
      };
    }, []);

    useTeamNotificationStream("bulkSupportTicketActionProgress", (event) => {
      if (event.status === "completed" || event.status === "failed") {
        const timeout = setTimeout(() => {
          setEventsByWorkflowId((previous) => {
            const next = { ...previous };
            delete next[event.workflowId];
            return next;
          });
          completionTimeouts.current.delete(event.workflowId);
        }, 1500);
        completionTimeouts.current.set(event.workflowId, timeout);
      }

      setEventsByWorkflowId((previous) => ({
        ...previous,
        [event.workflowId]: event,
      }));
    });

    const visibleEvents = useMemo(
      () =>
        Object.values(eventsByWorkflowId).filter(
          (event) =>
            event.status === "started" ||
            event.status === "processing" ||
            event.status === "completed",
        ),
      [eventsByWorkflowId],
    );

    if (visibleEvents.length === 0) {
      return null;
    }

    const dismissWorkflow = (workflowId: string) => {
      const timeout = completionTimeouts.current.get(workflowId);
      if (timeout) {
        clearTimeout(timeout);
        completionTimeouts.current.delete(workflowId);
      }
      setEventsByWorkflowId((prev) => {
        const next = { ...prev };
        delete next[workflowId];
        return next;
      });
    };

    return (
      <Flex className={styles.container} dir="column" gap="xl">
        {visibleEvents.map((event) => {
          const percentage =
            event.totalCount === 0
              ? 0
              : Math.round((event.processedCount / event.totalCount) * 100);

          return (
            <Flex
              bgColor="primary-alt"
              borderColor="primary"
              borderStyle="solid"
              borderWidth="1px"
              className={styles.card}
              dir="column"
              gap="xl"
              key={event.workflowId}
              p="xl"
              radius="sm"
            >
              <Flex align="center" justify="space-between">
                <Text fontSize="sm" fontWeight="bold">
                  {getActionLabel(event.action)}
                </Text>
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={XIcon}
                  onClick={() => dismissWorkflow(event.workflowId)}
                  size="sm"
                />
              </Flex>
              <RedoProgressBar
                labelStyle="text"
                percentage={percentage}
                style="thin"
              />
            </Flex>
          );
        })}
      </Flex>
    );
  },
);
